/*
 * monoblocc.jp — 構成選択ビーコン (variant intent beacon)
 *
 * WHY THIS EXISTS
 * Measured 2026-07-26: the Meta campaign pointing at this store produced 185 ad
 * landings, 59 ViewContent and 0 AddToCart. The ad set was optimising on Purchase
 * — an event this pixel has never once fired — so Meta had nothing to learn from
 * and spent 32% of its ¥3,000/day. The only other signal available, ViewContent,
 * fires on 97% of landings, which makes it a synonym for "arrived" rather than a
 * step. Between those two there was nothing at all.
 *
 * Choosing a 構成 is that missing step. This product is configured on two axes —
 * VESAプレートの種類 (2面/3面/4面) and マジックアームキットの数量 (1本/2本) — and
 * touching either is deliberate work, not a bounce. It maps onto Meta's STANDARD
 * `CustomizeProduct` event, so Ads Manager offers it as an optimisation target
 * directly, with no Custom Conversion to build or keep alive.
 *
 * WHY IT IS IN THE THEME AND NOT IN THE PIXEL
 * Shopify's Customer Events API has no `product_variant_selected`, so the funnel
 * pixel physically cannot see this. And the installed merchant custom pixel cannot
 * be updated programmatically at all: `WebPixelInput` carries `settings: JSON` and
 * no code, and the store token holds no write_pixels. The theme we *can* push. See
 * agent-infra/docs/monoblocc-shopify-store.md.
 *
 * A SIDE BENEFIT, AND IT IS THE IMPORTANT ONE: this runs in the page, not in
 * Shopify's opaque-origin pixel sandbox, so it can read the `_fbp` cookie the
 * Facebook channel pixel sets. `_fbp` is what Meta actually matches a person on.
 * The session id alone is a floor that makes an event *acceptable*; `_fbp` is what
 * makes it *useful*.
 *
 * WHAT IT WILL NOT DO
 * - Fire more than once per product per session. A signal that fires on every
 *   click is a rename of ViewContent, and that is the trap this exists to escape.
 * - Fire on Shopify's own initial render. `event.isTrusted` gates that.
 * - Throw. It runs on a live storefront; a bad beacon must cost a log line and
 *   nothing else.
 * - Send anything about the customer. Session id, handle, variant label. No PII.
 *
 * Rehearse without counting:  https://monoblocc.jp/products/magic-rig?mtid=mb_test
 * (api/store-events.js flags the row is_test and every dashboard filters it out.)
 */
(function () {
  "use strict";

  var ENDPOINT = "https://monoblocc-jp-store.vercel.app/api/store-events";
  var SESSION_KEY = "mb_store_session";
  var PARAMS_KEY = "mb_store_params";
  var SENT_PREFIX = "mb_variant_sent:";

  // Every param we issue at the ad, plus the click id. Anything else on the URL
  // is ignored rather than forwarded — the endpoint stores only these and there is
  // no reason for the browser to send more than the server will keep.
  var TRACKED_PARAMS = ["mtid", "utm_source", "utm_medium", "utm_campaign",
                        "utm_content", "utm_term", "fbclid"];

  function store(area) {
    // Safari in private mode throws on access rather than returning null.
    try { var s = window[area]; s.setItem("__mb", "1"); s.removeItem("__mb"); return s; }
    catch (e) { return null; }
  }
  var ss = store("sessionStorage");

  function readCookie(name) {
    try {
      var parts = ("; " + document.cookie).split("; " + name + "=");
      if (parts.length < 2) return null;
      return decodeURIComponent(parts.pop().split(";").shift()) || null;
    } catch (e) { return null; }
  }

  function id() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) { /* fall through */ }
    return "mb-" + Math.random().toString(36).slice(2) + "-" + String(Date.now());
  }

  /** One id for the whole visit. Shopify drops query params on internal
   *  navigation, so this is also what ties the配置 back to the landing. */
  function sessionId() {
    if (!ss) return id();               // storage blocked: still send, just unjoined
    var v = ss.getItem(SESSION_KEY);
    if (!v) { v = id(); ss.setItem(SESSION_KEY, v); }
    return v;
  }

  /**
   * The ad params, captured on whichever page of the session carried them and
   * remembered for the rest of it. Without this the beacon is attributed to
   * nothing the moment the visitor clicks anything, because the product page they
   * configure on is rarely the page they landed on.
   */
  function params() {
    var carried = {};
    try {
      var q = new URLSearchParams(window.location.search);
      TRACKED_PARAMS.forEach(function (k) {
        var v = q.get(k);
        if (v) carried[k] = String(v).slice(0, 200);
      });
    } catch (e) { /* no URLSearchParams */ }

    if (!ss) return carried;
    var saved = {};
    try { saved = JSON.parse(ss.getItem(PARAMS_KEY) || "{}") || {}; } catch (e) { saved = {}; }
    // A fresh param on this URL wins; anything absent falls back to the session.
    var merged = {};
    Object.keys(saved).forEach(function (k) { merged[k] = saved[k]; });
    Object.keys(carried).forEach(function (k) { merged[k] = carried[k]; });
    try { ss.setItem(PARAMS_KEY, JSON.stringify(merged)); } catch (e) { /* full */ }
    return merged;
  }

  /** `/products/magic-rig` -> `magic-rig`, or null anywhere that is not a product. */
  function productHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * What they picked, as the shopper sees it: "3面 / 2本".
   *
   * Read off the form rather than from a variant lookup, because the label is the
   * thing worth reporting and a variant id would need a product JSON blob that
   * Dawn does not reliably render.
   */
  function selection(form) {
    var out = [];
    try {
      var scope = form || document;
      scope.querySelectorAll('variant-selects select, .product-form__input select').forEach(function (el) {
        if (el.value) out.push(el.value.trim());
      });
      scope.querySelectorAll('variant-selects input[type="radio"]:checked, .product-form__input input[type="radio"]:checked').forEach(function (el) {
        if (el.value) out.push(el.value.trim());
      });
    } catch (e) { /* selector unsupported */ }
    // De-duplicate while keeping order — Dawn renders both a select and radios on
    // some layouts and we do not want "3面 / 3面 / 2本".
    var seen = {};
    return out.filter(function (v) {
      if (seen[v]) return false;
      seen[v] = true;
      return true;
    }).join(" / ").slice(0, 120);
  }

  function send(handle, variant) {
    var body = {
      ctx: {
        sessionId: sessionId(),
        path: window.location.pathname,
        url: window.location.href.slice(0, 500),
        params: params(),
        // Read here rather than server-side: these are first-party cookies on
        // monoblocc.jp and the API function never sees them.
        fbp: readCookie("_fbp"),
        fbc: readCookie("_fbc")
      },
      events: [{
        name: "variant_selected",
        ts: new Date().toISOString(),
        data: { handle: handle, variant: variant }
      }]
    };
    try {
      // fetch+keepalive, NOT sendBeacon: this is a cross-origin JSON POST, so it
      // preflights, and sendBeacon cannot preflight — it would fail silently.
      // keepalive still survives the page unload that a click often starts.
      fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
        mode: "cors",
        credentials: "omit"
      }).catch(function () { /* a lost beacon is not the storefront's problem */ });
    } catch (e) { /* fetch missing */ }
  }

  function onChange(event) {
    try {
      // Shopify's own render dispatches change events too. Only a person counts.
      if (!event || !event.isTrusted) return;
      var el = event.target;
      if (!el || !el.closest) return;
      if (!el.closest('variant-selects, .product-form__input')) return;

      var handle = productHandle();
      if (!handle) return;

      // Once per product per session. Reconfiguring the same rig six times is one
      // decision, not six conversions, and Meta would learn the wrong thing from
      // a signal that scales with fidgeting.
      var key = SENT_PREFIX + handle;
      if (ss && ss.getItem(key)) return;
      if (ss) ss.setItem(key, "1");

      send(handle, selection(el.closest("form")));
    } catch (e) {
      if (window.console && console.debug) console.debug("[mb-variant-intent]", e && e.message);
    }
  }

  // Delegated and capturing, so it survives Dawn re-rendering the picker after
  // every selection — the elements bound at load are gone by the second click.
  document.addEventListener("change", onChange, true);
})();
